import { useCallback, useState } from "react";
import { AxiosResponse } from "axios";
import {
  downloadSpecialOrderBlankForm,
  downloadSpecialOrderDocuments,
} from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../SalesReportExport/utils";
import { SpecialOrderDocumentSheets } from "../../types/special-order.type";

export const SPECIAL_ORDER_DOCUMENT_ERROR =
  "ไม่สามารถดาวน์โหลดเอกสารได้ กรุณาลองใหม่อีกครั้ง";

type DocumentAction = "print" | SpecialOrderDocumentSheets;

/** Same test as usePdfDownload: a failure with an HTTP status already has its message. */
const handledByInterceptor = (error: unknown) =>
  typeof (error as { response?: { status?: number } } | null)?.response?.status === "number";

const asPdf = (raw: unknown): Blob => {
  if (!(raw instanceof Blob) || raw.size === 0 || raw.type.includes("application/json")) {
    throw new Error("Invalid PDF file response");
  }
  return new Blob([raw], { type: "application/pdf" });
};

/**
 * Opens a fetched PDF in a new tab for the browser's own viewer to print. The tab is opened
 * synchronously, inside the click, so popup blockers let it through.
 */
const printPdf = async (fetchPdf: () => Promise<AxiosResponse<Blob>>) => {
  const tab = window.open("", "_blank");
  try {
    const response = await fetchPdf();
    const url = URL.createObjectURL(asPdf(response.data));
    if (tab) tab.location.href = url;
    else window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    if (tab) tab.close();
    if (!handledByInterceptor(error)) pushGlobalError(SPECIAL_ORDER_DOCUMENT_ERROR);
  }
};

/**
 * The A4 set of a special order. The endpoint is authenticated, so the PDF is fetched as a
 * blob through the shared axios client and handed to the browser — never by opening the API
 * URL. Printing opens it in a new tab where the browser's own viewer prints it.
 */
export function useSpecialOrderDocuments(orderUuid: string, orderRef: string) {
  const [busy, setBusy] = useState<DocumentAction | null>(null);

  const download = useCallback(
    async (sheets: SpecialOrderDocumentSheets) => {
      if (busy) return;
      setBusy(sheets);
      try {
        const response = await downloadSpecialOrderDocuments(orderUuid, sheets);
        const blob = asPdf(response.data);
        const filename =
          getFilenameFromContentDisposition(
            response.headers["content-disposition"] as string | undefined,
          ) || `${orderRef}-${sheets}.pdf`;
        downloadBlob(filename, blob);
      } catch (error) {
        if (!handledByInterceptor(error)) pushGlobalError(SPECIAL_ORDER_DOCUMENT_ERROR);
      } finally {
        setBusy(null);
      }
    },
    [busy, orderUuid, orderRef],
  );

  const print = useCallback(async () => {
    if (busy) return;
    setBusy("print");
    try {
      await printPdf(() => downloadSpecialOrderDocuments(orderUuid, "all"));
    } finally {
      setBusy(null);
    }
  }, [busy, orderUuid]);

  return { busy, download, print };
}

/** The customer's blank hand-written order form, opened in a new tab for printing. */
export function useSpecialOrderBlankForm(customerUuid: string) {
  const [busy, setBusy] = useState<boolean>(false);

  const print = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await printPdf(() => downloadSpecialOrderBlankForm(customerUuid));
    } finally {
      setBusy(false);
    }
  }, [busy, customerUuid]);

  return { busy, print };
}
